import {
    Box,
    SimpleGrid,
    Text,
    Flex,
    Icon,
    Container,
    Alert,
    AlertIcon,
    Button,
    HStack,
    Input,
} from "@chakra-ui/react";
import PageLoader from "./components/PageLoader";
import TraderRetention from "./TraderRetention";
import {
    FaUsers,
    FaChartLine,
    FaUserCheck,
    FaExchangeAlt,
    FaChartBar,
    FaMoneyBillWave,
    FaRobot,
    FaBolt,
} from "react-icons/fa";
import api from "./api";
import { useEffect, useRef, useState } from "react";
import type { IconType } from "react-icons";

const DATE_PRESETS = [
    { key: "today", label: "Today" },
    { key: "yesterday", label: "Yesterday" },
    { key: "week", label: "This Week" },
    { key: "month", label: "This Month" },
    { key: "lastMonth", label: "Last Month" },
    { key: "3m", label: "3 Months" },
    { key: "6m", label: "6 Months" },
    { key: "year", label: "1 Year" },
    { key: "all", label: "All" },
    { key: "custom", label: "Custom" },
] as const;
type DatePreset = (typeof DATE_PRESETS)[number]["key"];

// Local calendar date (YYYY-MM-DD) for <input type="date">
function formatDateForInput(date: Date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

const startOfDay = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());
const endOfDay = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

// Preset -> local-time range; a null bound means unbounded
function getPresetRange(preset: Exclude<DatePreset, "custom">): {
    start: Date | null;
    end: Date | null;
} {
    const now = new Date();
    const today = startOfDay(now);
    const monthsAgo = (n: number) =>
        new Date(today.getFullYear(), today.getMonth() - n, today.getDate());
    switch (preset) {
        case "today":
            return { start: today, end: endOfDay(now) };
        case "yesterday": {
            const y = new Date(today);
            y.setDate(y.getDate() - 1);
            return { start: y, end: endOfDay(y) };
        }
        case "week": {
            // Week starts on Monday
            const monday = new Date(today);
            monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
            return { start: monday, end: endOfDay(now) };
        }
        case "month":
            return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: endOfDay(now) };
        case "lastMonth":
            return {
                start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
                end: endOfDay(new Date(now.getFullYear(), now.getMonth(), 0)),
            };
        case "3m":
            return { start: monthsAgo(3), end: endOfDay(now) };
        case "6m":
            return { start: monthsAgo(6), end: endOfDay(now) };
        case "year":
            return { start: monthsAgo(12), end: endOfDay(now) };
        case "all":
            return { start: null, end: null };
    }
}

function Dashboard() {
    const [dashboardStats, setDashboardStats] = useState<any>(null);
    const [hlAgentCount, setHlAgentCount] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [preset, setPreset] = useState<DatePreset>("month");
    const [customRange, setCustomRange] = useState(() => {
        const today = formatDateForInput(new Date());
        return { startDate: today, endDate: today };
    });
    // Ignore responses from a range the user has already moved away from
    const requestId = useRef(0);

    const fetchDashboardStats = async (params: Record<string, string>) => {
        const id = ++requestId.current;
        setLoading(true);
        setError("");
        try {
            const res = await api.get("/api/admin/dashboard/stats", { params });
            if (id !== requestId.current) return;
            setDashboardStats(res?.data?.data || null);
        } catch (err: any) {
            if (id !== requestId.current) return;
            console.error("Error fetching dashboard stats:", {
                message: err.message,
                response: err.response?.data,
                status: err.response?.status,
                url: err.config?.url,
            });

            const errorMessage =
                err.response?.status === 404
                    ? "Dashboard endpoint not found. Please restart the backend server."
                    : err.response?.data?.message ||
                      err.message ||
                      "Error loading dashboard stats";

            setError(errorMessage);
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    };

    const fetchHlAgents = async () => {
        try {
            const agentsRes = await api.get("/api/hyperliquid/agents?limit=1");
            setHlAgentCount(agentsRes.data?.data?.pagination?.total ?? 0);
        } catch {
            // non-fatal — HL stats are supplementary
        }
    };

    useEffect(() => {
        fetchHlAgents();
    }, []);

    useEffect(() => {
        const params: Record<string, string> = {};
        if (preset === "custom") {
            if (customRange.startDate) {
                params.startDate = new Date(`${customRange.startDate}T00:00:00`).toISOString();
            }
            if (customRange.endDate) {
                params.endDate = new Date(`${customRange.endDate}T23:59:59.999`).toISOString();
            }
        } else {
            const { start, end } = getPresetRange(preset);
            if (start) params.startDate = start.toISOString();
            if (end) params.endDate = end.toISOString();
        }
        fetchDashboardStats(params);
    }, [preset, customRange]);

    // Format number with K/M/B/T suffixes for large numbers
    const formatNumber = (num: number | string) => {
        // Convert string to number if needed
        const value = typeof num === "string" ? parseFloat(num) : num;

        if (!value || value === 0 || isNaN(value) || !isFinite(value))
            return "0.00";

        // If number is extremely small (less than 0.000001), show as 0.00
        if (Math.abs(value) < 0.000001) {
            return "0.00";
        }

        // Format with appropriate suffix
        const absValue = Math.abs(value);
        if (absValue >= 1e12) {
            return (value / 1e12).toFixed(2) + "T";
        } else if (absValue >= 1e9) {
            return (value / 1e9).toFixed(2) + "B";
        } else if (absValue >= 1e6) {
            return (value / 1e6).toFixed(2) + "M";
        } else if (absValue >= 1e3) {
            return (value / 1e3).toFixed(2) + "K";
        }

        // For smaller numbers, show with 2 decimal places
        return value.toFixed(2);
    };

    const usd = (value: number | undefined) => `$${formatNumber(value ?? 0)}`;

    const statCards: {
        label: string;
        value: number | string;
        icon: IconType;
        color: string;
        note?: string;
    }[] = [
        {
            label: "Wallet Connects",
            value: dashboardStats?.walletConnects ?? 0,
            icon: FaUsers,
            color: "blue.500",
        },
        {
            label: "Active Wallets",
            value: dashboardStats?.activeWallets ?? 0,
            icon: FaUserCheck,
            color: "green.500",
        },
        {
            label: "Trading Volume",
            value: usd(dashboardStats?.volume?.all),
            icon: FaChartLine,
            color: "red.500",
        },
        {
            label: "All Trades",
            value: dashboardStats?.trades?.all ?? 0,
            icon: FaExchangeAlt,
            color: "purple.500",
            note: "GMX + Hyperliquid",
        },
        {
            label: "GMX Trades",
            value: dashboardStats?.trades?.gmx ?? 0,
            icon: FaExchangeAlt,
            color: "blue.500",
            note: `Volume ${usd(dashboardStats?.volume?.gmx)}`,
        },
        {
            label: "HL Trades",
            value: dashboardStats?.trades?.hyperliquid ?? 0,
            icon: FaExchangeAlt,
            color: "yellow.500",
            note: `Volume ${usd(dashboardStats?.volume?.hyperliquid)}`,
        },
        {
            label: "Avg Volume/Trade",
            value: usd(dashboardStats?.avgVolumePerTrade),
            icon: FaChartBar,
            color: "orange.500",
        },
        {
            label: "Fees Generated",
            value: usd(dashboardStats?.fees?.usd),
            icon: FaMoneyBillWave,
            color: "teal.500",
        },
        {
            label: "HL Total Fills",
            value: dashboardStats?.hlFills ?? 0,
            icon: FaBolt,
            color: "yellow.500",
        },
        {
            label: "HL Active Agents",
            value: hlAgentCount !== null ? hlAgentCount : "—",
            icon: FaRobot,
            color: "cyan.500",
            note: "All time",
        },
    ];

    return (
        <Container maxW="7xl" py={6}>
            {/* Header */}
            <Box mb={8}>
                <Text fontSize="3xl" fontWeight="700" color="white" mb={2}>
                    Dashboard Overview
                </Text>
                <Text color="gray.400" fontSize="lg">
                    Real-time analytics and key performance metrics
                </Text>
            </Box>


            {/* Date filter */}
            <Flex wrap="wrap" gap={2} align="center" mb={6}>
                {DATE_PRESETS.map(({ key, label }) => (
                    <Button
                        key={key}
                        size="sm"
                        variant={preset === key ? "solid" : "outline"}
                        colorScheme={preset === key ? "blue" : "gray"}
                        onClick={() => setPreset(key)}
                    >
                        {label}
                    </Button>
                ))}
                {preset === "custom" && (
                    <HStack spacing={2}>
                        <Input
                            type="date"
                            size="sm"
                            maxW="170px"
                            value={customRange.startDate}
                            max={customRange.endDate || undefined}
                            onChange={(e) =>
                                setCustomRange({ ...customRange, startDate: e.target.value })
                            }
                        />
                        <Text color="gray.400" fontSize="sm">
                            to
                        </Text>
                        <Input
                            type="date"
                            size="sm"
                            maxW="170px"
                            value={customRange.endDate}
                            min={customRange.startDate || undefined}
                            onChange={(e) =>
                                setCustomRange({ ...customRange, endDate: e.target.value })
                            }
                        />
                    </HStack>
                )}
            </Flex>

            {loading && !dashboardStats && <PageLoader message="Loading dashboard stats..." />}

            {error && (
                <Alert status="error" mb={6}>
                    <AlertIcon />
                    {error}
                </Alert>
            )}

            {/* Stats Grid */}
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {statCards.map((stat, index) => (
                    <Box
                        key={index}
                        bg="gray.800"
                        border="1px solid"
                        borderColor="gray.700"
                        borderRadius="12px"
                        p={6}
                        position="relative"
                        overflow="hidden"
                        _hover={{
                            borderColor: "gray.600",
                            transform: "translateY(-2px)",
                            boxShadow: "xl",
                        }}
                        transition="all 0.2s ease-in-out"
                    >
                        {/* Gradient overlay */}
                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            height="2px"
                            bgGradient={`linear(to-r, ${
                                stat.color
                            }, ${stat.color.replace(".500", ".300")})`}
                        />

                        <Flex justify="space-between" align="start" mb={4}>
                            <Box>
                                <Icon
                                    as={stat.icon}
                                    w={8}
                                    h={8}
                                    color={stat.color}
                                    mb={3}
                                />
                                <Text
                                    fontSize="sm"
                                    fontWeight="500"
                                    color="gray.400"
                                    textTransform="uppercase"
                                    letterSpacing="wide"
                                >
                                    {stat.label}
                                </Text>
                            </Box>
                        </Flex>

                        <Text
                            fontSize="2xl"
                            fontWeight="700"
                            color="white"
                            lineHeight={1}
                            opacity={loading ? 0.5 : 1}
                        >
                            {typeof stat.value === "string"
                                ? stat.value
                                : stat.value.toLocaleString()}
                        </Text>

                        {stat.note && (
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                {stat.note}
                            </Text>
                        )}
                    </Box>
                ))}
            </SimpleGrid>

            <TraderRetention />

            {/* Additional Info */}
            <Box
                mt={8}
                p={6}
                bg="gray.800"
                borderRadius="12px"
                border="1px solid"
                borderColor="gray.700"
            >
                <Flex align="center" justify="space-between">
                    <Box>
                        <Text
                            fontSize="lg"
                            fontWeight="600"
                            color="white"
                            mb={1}
                        >
                            System Status
                        </Text>
                        <Text color="gray.400" fontSize="sm">
                            All systems operational
                        </Text>
                    </Box>
                    <Box
                        w={3}
                        h={3}
                        bg="green.400"
                        borderRadius="full"
                        boxShadow="0 0 10px rgba(72, 187, 120, 0.5)"
                    />
                </Flex>
            </Box>
        </Container>
    );
}

export default Dashboard;
